import type {
  AgentBillingPlan,
  AgentBillingSubscriptionInterval,
  SubscriptionView,
} from "schemas/agent-billing";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { Toggle } from "@replohq/lattice/components/toggle/Toggle";
import { ToggleGroup } from "@replohq/lattice/components/toggle/ToggleGroup";
import { cn } from "@replohq/lattice/utils/cn";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { DebugOverlay } from "@v2/features/admin/DebugOverlay";
import { useDebugOverlayIsVisible } from "@v2/features/admin/debugPanelChrome";
import { showAgentBillingUpdateSuccessToast } from "@v2/features/agent-billing/agentBillingToasts";
import useAgentApplySubscriptionChangeMutation from "@v2/features/agent-billing/hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "@v2/features/agent-billing/hooks/useAgentSubscription";
import { useRateCards } from "@v2/features/agent-billing/hooks/useRateCards";
import {
  BillingSeparator,
  ConfirmFooter,
  LineItemRow,
  SubtotalRow,
} from "@v2/features/agent-billing/pricing/BillingModalComponents";
import { PRODUCT_DISPLAY_CONFIG } from "@v2/features/agent-billing/productDisplayConfig";
import { price } from "@v2/features/agent-billing/utils/price";
import { rateCard as rateCardUtils } from "@v2/features/agent-billing/utils/rateCard";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { formatPrice } from "replo-utils/lib/format";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { PLAN_PRODUCT_TYPES } from "schemas/agent-billing";

import { SubscriptionDraft } from "../models/SubscriptionDraft";
import {
  getPlanChangeCtaButtonLabel,
  getPlanChangeType,
} from "../utils/planChangeLabels";

type AgentPlanChangeConfirmationModalProps = {
  subscriptionView: SubscriptionView;
  onBillingIntervalChange?: (
    interval: AgentBillingSubscriptionInterval,
  ) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const PlanChangeConfirmationModal: React.FC<
  AgentPlanChangeConfirmationModalProps
> = ({ subscriptionView, onBillingIntervalChange, open, onOpenChange }) => {
  const workspace = useCurrentWorkspace();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { visible: isDebugView } = useDebugOverlayIsVisible();
  const lineItemsRef = React.useRef<HTMLDivElement>(null);

  const incomingPlanSubscriptionItem = subscriptionView.subscriptionItems.find(
    (subscriptionItem) => {
      return PLAN_PRODUCT_TYPES.has(subscriptionItem.productType);
    },
  );
  // Legacy cast - may or may not be necessary
  // eslint-disable-next-line replo/no-as-cast
  const incomingPlan = (incomingPlanSubscriptionItem?.productType ??
    "starter") as AgentBillingPlan;
  const incomingInterval = incomingPlanSubscriptionItem?.interval ?? "month";

  const [billingInterval, setBillingInterval] =
    useOverridableState<AgentBillingSubscriptionInterval>(
      incomingInterval,
      onBillingIntervalChange,
    );

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  const formattedNextBillingDate = React.useMemo(() => {
    if (activeSubscription?.currentPeriodEnd) {
      return new Date(activeSubscription.currentPeriodEnd).toLocaleDateString(
        undefined,
        {
          month: "short",
          day: "numeric",
          year: "numeric",
        },
      );
    }
    const futureDate = new Date();
    if (billingInterval === "year") {
      futureDate.setFullYear(futureDate.getFullYear() + 1);
    } else {
      futureDate.setMonth(futureDate.getMonth() + 1);
    }
    return futureDate.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [activeSubscription?.currentPeriodEnd, billingInterval]);

  // Defense-in-depth: entry points are gated, but never let a custom-contract workspace reach the confirm flow. (yuxin, 2026-07-14, REPL-29329)
  if (!workspace || !rateCards || activeSubscription?.hasCustomPricing) {
    return null;
  }

  const rateCard = rateCards.find((rateCard) => rateCard.plan === incomingPlan);
  if (!rateCard) {
    return null;
  }

  const modalGraphicSrc = PRODUCT_DISPLAY_CONFIG[incomingPlan].modalGraphicSrc;
  const isPlanCanceled = Boolean(activeSubscription?.cancelAt);

  const nextSubscriptionView = SubscriptionDraft.fromView(
    subscriptionView,
  ).withInterval(billingInterval, { rateCard }).current;

  const currentRateCard = currentDraft
    ? rateCards.find((rateCard) => rateCard.plan === currentDraft.plan)
    : null;

  const currentPlanMonthlyPrice =
    currentRateCard && currentDraft
      ? rateCardUtils.getSubscriptionMonthlyTotal({
          rateCard: currentRateCard,
          subscriptionView: currentDraft.current,
        })
      : null;

  const modalType = getPlanChangeType({
    currentPlan: currentDraft?.plan ?? null,
    nextPlan: incomingPlan,
    isPlanCanceled,
  });

  const isIntervalUpgrade =
    activeSubscription?.interval === "month" && billingInterval === "year";

  const hasChanges = (() => {
    if (!currentDraft) {
      return true;
    }
    const currentSubscriptionItems = currentDraft.current.subscriptionItems;
    const nextSubscriptionItems = nextSubscriptionView.subscriptionItems;
    if (currentSubscriptionItems.length !== nextSubscriptionItems.length) {
      return true;
    }
    return nextSubscriptionItems.some((nextSubscriptionItem) => {
      const currentSubscriptionItem = currentSubscriptionItems.find(
        (subscriptionItem) => {
          return (
            subscriptionItem.productType === nextSubscriptionItem.productType
          );
        },
      );
      return (
        !currentSubscriptionItem ||
        currentSubscriptionItem.quantity !== nextSubscriptionItem.quantity ||
        currentSubscriptionItem.interval !== nextSubscriptionItem.interval
      );
    });
  })();

  const planSubscriptionItem = nextSubscriptionView.subscriptionItems.find(
    (subscriptionItem) => {
      return PLAN_PRODUCT_TYPES.has(subscriptionItem.productType);
    },
  );
  if (!planSubscriptionItem) {
    return null;
  }
  const basePrice =
    rateCard.prices.find(
      (priceEntry) => priceEntry.priceId === planSubscriptionItem.priceId,
    ) ??
    price.findDefaultByInterval({
      prices: rateCard.prices,
      interval: billingInterval,
    });
  if (!basePrice) {
    return null;
  }
  const newCorePlanPrice = price.getPerMonth(basePrice);

  const toastTitle = exhaustiveSwitch({ type: modalType })({
    upgrade: `Upgraded to ${rateCard.title} plan`,
    downgrade: `Downgraded to ${rateCard.title} plan`,
    "change-plan": `${rateCard.title} plan updated`,
    "resume-plan": `Your ${rateCard.title} plan has been resumed`,
  });

  const isUpgrade = modalType === "upgrade";

  const handleConfirm = async () => {
    try {
      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set("toast_title", toastTitle);
      if (isUpgrade) {
        successUrl.searchParams.set("show_confetti", "true");
      }

      const subscriptionUpdateResult = await applySubscriptionChange({
        workspaceId: workspace.id,
        subscriptionView: nextSubscriptionView,
        successUrl: successUrl.toString(),
        cancelUrl: window.location.href,
      });
      if (!subscriptionUpdateResult.confirmationRequired) {
        showAgentBillingUpdateSuccessToast(toastTitle, {
          showConfetti: isUpgrade,
        });
        onOpenChange(false);
      }
    } catch {
      // Error toast is shown by the global tRPC error handler (handleTRPCClientError)
    }
  };

  const addOnLineItems = rateCardUtils.getLineItems({
    rateCard,
    subscriptionView: nextSubscriptionView,
  });

  const newPlanTotalPricePerMonth = rateCardUtils.getSubscriptionMonthlyTotal({
    rateCard,
    subscriptionView: nextSubscriptionView,
  });
  if (!newPlanTotalPricePerMonth) {
    return null;
  }
  const formattedTotalPrice = `${formatPrice(newPlanTotalPricePerMonth)}/mo`;

  const totalBilledAnnually =
    billingInterval === "year"
      ? rateCardUtils.getSubscriptionYearTotal({
          rateCard,
          subscriptionView: nextSubscriptionView,
        })
      : null;

  const { buttonLabel, isNoOp } = getPlanChangeCtaButtonLabel({
    currentPlan: currentDraft?.plan ?? null,
    nextPlan: incomingPlan,
    planName: rateCard.title,
    isPlanCanceled,
    isIntervalUpgrade,
    hasChanges,
  });
  const confirmButtonText = isNoOp
    ? buttonLabel
    : `${buttonLabel} (${formattedTotalPrice})`;

  const headerText = exhaustiveSwitch({ type: modalType })({
    upgrade: (
      <>
        Level up to <span className="italic">{rateCard.title}</span>
      </>
    ),
    downgrade: (
      <>
        Downgrade to <span className="italic">{rateCard.title}</span>
      </>
    ),
    "change-plan": (
      <>
        Update your <span className="italic">{rateCard.title}</span> plan
      </>
    ),
    "resume-plan": (
      <>
        Resume your <span className="italic">{rateCard.title}</span> plan
      </>
    ),
  });

  const percentSavedBySwitchingToYearly = rateCardUtils.getPercentSaved({
    rateCard,
  });

  const isResumingPlan = modalType === "resume-plan";
  const isCurrentlyAnnual = activeSubscription?.interval === "year";

  return (
    <SplitModal
      open={open}
      onOpenChange={onOpenChange}
      media={
        <img
          src={modalGraphicSrc}
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      <div className="flex flex-1 flex-col justify-between gap-6">
        <div ref={lineItemsRef} className="flex flex-col gap-6">
          <div className="text-3xl font-editorial">{headerText}</div>

          {!isResumingPlan && !isCurrentlyAnnual && (
            <ToggleGroup
              value={[billingInterval]}
              onValueChange={(values) => {
                if (values.length > 0) {
                  setBillingInterval(
                    // Legacy cast - may or may not be necessary
                    // eslint-disable-next-line replo/no-as-cast
                    values[0] as AgentBillingSubscriptionInterval,
                  );
                }
              }}
              className="w-full"
            >
              <Toggle
                value="year"
                size="base"
                className="flex-1"
                UNSAFE_className="justify-center"
              >
                Annual ({percentSavedBySwitchingToYearly}% Off)
              </Toggle>
              <Toggle
                value="month"
                size="base"
                className="flex-1"
                UNSAFE_className="justify-center"
              >
                Monthly
              </Toggle>
            </ToggleGroup>
          )}

          <PlanLineItems
            planTitle={rateCard.title}
            billingInterval={billingInterval}
            newCorePlanPrice={newCorePlanPrice}
            formattedNextBillingDate={formattedNextBillingDate}
            addOnLineItems={addOnLineItems}
            currentPlanMonthlyPrice={currentPlanMonthlyPrice}
            newPlanTotalPricePerMonth={newPlanTotalPricePerMonth}
            totalBilledAnnually={totalBilledAnnually}
            percentSaved={percentSavedBySwitchingToYearly}
          />
        </div>
        <DebugOverlay
          isVisible={isDebugView}
          referenceRef={lineItemsRef}
          data={{
            plan: incomingPlan,
            interval: billingInterval,
            basePriceId: basePrice.priceId,
            basePriceInterval: basePrice.interval,
            subscriptionItems: nextSubscriptionView.subscriptionItems,
          }}
        />

        <ConfirmFooter
          buttonText={confirmButtonText}
          buttonVariant={isNoOp ? "secondary" : "primary"}
          onConfirm={handleConfirm}
          isLoading={isLoadingSubscriptionUpdate}
          disabled={isNoOp}
        />
      </div>
    </SplitModal>
  );
};

const PlanLineItems = ({
  planTitle,
  billingInterval,
  newCorePlanPrice,
  formattedNextBillingDate,
  addOnLineItems,
  currentPlanMonthlyPrice,
  newPlanTotalPricePerMonth,
  totalBilledAnnually,
  percentSaved,
}: {
  planTitle: string;
  billingInterval: AgentBillingSubscriptionInterval;
  newCorePlanPrice: { amount: number; currency: string };
  formattedNextBillingDate: string;
  addOnLineItems: {
    label: string;
    amount: number;
    currency: string;
  }[];
  currentPlanMonthlyPrice: { amount: number; currency: string } | null;
  newPlanTotalPricePerMonth: { amount: number; currency: string };
  totalBilledAnnually: { amount: number; currency: string } | null;
  percentSaved: number;
}) => (
  <div className="flex flex-col gap-4 text-sm">
    <div className="flex flex-col gap-1">
      <LineItemRow
        label={
          <span className="flex items-center gap-2 font-medium">
            {planTitle} Plan
            <Tag size="base" variant="v2">
              {billingInterval === "year" ? "Annual" : "Monthly"}
            </Tag>
          </span>
        }
        value={
          <span className="font-semibold">
            {formatPrice(newCorePlanPrice)}/mo
          </span>
        }
      />
      <div className="text-muted">Renews {formattedNextBillingDate}</div>
    </div>
    {addOnLineItems.map((lineItem, index) => (
      <LineItemRow
        key={index}
        label={<span className="text-muted">{lineItem.label}</span>}
        value={
          <span className="font-semibold">
            {formatPrice({
              amount: lineItem.amount,
              currency: lineItem.currency,
            })}
            /mo
          </span>
        }
      />
    ))}

    <BillingSeparator />

    <div className="flex flex-col gap-1">
      <SubtotalRow
        currentMonthlyPrice={currentPlanMonthlyPrice}
        newMonthlyPrice={newPlanTotalPricePerMonth}
      />
      <p
        className={cn(
          "text-xs text-placeholder",
          totalBilledAnnually !== null && percentSaved > 0
            ? "visible"
            : "invisible",
        )}
      >
        You'll be billed{" "}
        {totalBilledAnnually !== null ? formatPrice(totalBilledAnnually) : ""}{" "}
        once (and save up to {percentSaved}%)
      </p>
    </div>
  </div>
);
